import React from 'react';
import {
  FormHandlers, FormSkeleton, Message, useNotifications,
} from '@doveit/bricks';
import { Agency, Agent } from '../../../providers/api/dtos';
import useAgents from '../../../hooks/use-agents/useAgents';
import AgencyTeamForm, { AgencyTeamFormProps } from '../../components/agency-team-form/AgencyTeamForm';
import { updateAgency } from '../../../providers/api/agency/agencyProvider';
import { AgentStatus } from '../../../domain/types';

export interface UpdateAgencyTeamProps {
  agency: Agency,
  formRef?: React.MutableRefObject<FormHandlers>,
  onSuccess?: (agency: Agency) => void,
}

const LOAD_TEAM_MEMBERS_ERROR_MESSAGE = 'Non è stato possibile caricare i componenti del team.';
const SAVE_TEAM_MEMBERS_ERROR_MESSAGE = 'Non è stato possibile salvare i componenti del team.';
const SAVE_TEAM_MEMBERS_SUCCESS_MESSAGE = 'Componenti del team salvati con successo.';

const UpdateAgencyTeam: React.FC<UpdateAgencyTeamProps> = ({
  agency,
  formRef,
  onSuccess,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const hasTeamMembers = React.useMemo(
    () => agency.agentIds.length > 0,
    [agency],
  );
  const {
    data: existingAgents,
    error: existingAgentsError,
    isLoading: existingAgentsLoading,
    mutate: mutateExistingAgents,
  } = useAgents(hasTeamMembers ? { agencyId: agency.id!, active: AgentStatus.ACTIVE } : undefined);

  const { addSuccess, addError } = useNotifications();

  const onSubmit: AgencyTeamFormProps['onSubmit'] = React.useCallback(async ({ agents }) => {
    setIsSaving(true);

    try {
      const updatedAgency = await updateAgency(agency.id!, { ...agency, agentIds: agents.map((a: Agent) => a.id!) });

      setIsSaving(false);
      addSuccess(SAVE_TEAM_MEMBERS_SUCCESS_MESSAGE);

      await mutateExistingAgents();

      onSuccess?.(updatedAgency);
    } catch (error) {
      setIsSaving(false);
      addError(SAVE_TEAM_MEMBERS_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, agency, mutateExistingAgents, onSuccess]);

  return (
    <>
      {(hasTeamMembers && existingAgentsLoading) && (
        <FormSkeleton aria-label="Caricamento componenti team" />
      )}
      {(hasTeamMembers && existingAgents) && (
        <AgencyTeamForm
          onSubmit={onSubmit}
          innerRef={formRef}
          initialValues={{ agents: existingAgents.content }}
          loading={isSaving}
        />
      )}
      {existingAgentsError && (
        <Message type="critical" message={LOAD_TEAM_MEMBERS_ERROR_MESSAGE} />
      )}
      {!hasTeamMembers && (
        <AgencyTeamForm
          innerRef={formRef}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};

export default UpdateAgencyTeam;
